import axios, { AxiosError } from 'axios';
import { enqueueSnackbar } from 'notistack';
import { queryClient } from './queryKeys';

const api = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL,
});

let isRedirecting = false;

// Support both localStorage and sessionStorage for token retrieval
api.interceptors.request.use(
  config => {
    let token = localStorage.getItem('access_token');
    if (!token) {
      token = sessionStorage.getItem('access_token');
    }

    // Only add authorization header if we have a valid token
    // This prevents unauthenticated requests from being treated as 401
    if (token && token.trim() !== '' && token !== 'undefined' && token !== 'null') {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  error => Promise.reject(error)
);

// Handle authentication errors (401, 403)
api.interceptors.response.use(
  (response) => {
    // Reset redirect flag on successful response
    isRedirecting = false;
    return response;
  },
  (error: AxiosError) => {
    const status = error.response?.status;

    // DEBUG: Log all errors
    console.log('🔍 API Error:', {
      status,
      url: error.config?.url,
      hasAuthHeader: !!error.config?.headers?.Authorization,
    });

    // Handle authentication errors (401, 403) - but only if NOT due to missing token
    if (status === 401 || status === 403) {
      // Check if this request had no authorization header
      // If it didn't have auth header, don't treat as session expiry
      if (!error.config?.headers?.Authorization) {
        console.log('⚠️  401/403 without auth header - likely public endpoint, skipping redirect');
        return Promise.reject(error);
      }

      // Prevent multiple redirects
      if (isRedirecting) {
        return Promise.reject(error);
      }

      const currentPath = window.location.pathname;
      const publicPaths = [
        '/login',
        '/sign-up',
        '/signup',
        '/register',
        '/forgot-password',
        '/reset-password',
        '/otp/',
        '/new-password',
        '/checkout/callback',
        '/checkout/success'
      ];

      // Don't redirect if already on a public page
      if (publicPaths.some(path => currentPath.includes(path))) {
        return Promise.reject(error);
      }

      isRedirecting = true;
      console.log('🔴 Session expired - redirecting to login');

      // Clear all authentication data
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('is_login');
      localStorage.removeItem('user');
      sessionStorage.removeItem('access_token');
      sessionStorage.removeItem('refresh_token');

      // Clear Recoil persist storage
      const recoilKeys = ['recoil-persist'];
      recoilKeys.forEach(key => {
        localStorage.removeItem(key);
        sessionStorage.removeItem(key);
      });

      // Clear React Query cache
      try {
        queryClient.clear();
      } catch (e) {
        // queryClient might not be initialized yet
        console.warn('Could not clear query client');
      }

      // Show notification to user
      enqueueSnackbar('Your session has expired. Please login again.', {
        variant: 'warning',
        autoHideDuration: 4000,
      });

      // Save the current URL for redirect after login
      const returnUrl = window.location.pathname + window.location.search;
      if (returnUrl !== '/login' && returnUrl !== '/') {
        localStorage.setItem('return_url', returnUrl);
      }

      // Redirect to login after a brief delay
      setTimeout(() => {
        window.location.href = `/login?redirect=${encodeURIComponent(returnUrl)}`;
        isRedirecting = false;
      }, 500);

      return Promise.reject(error);
    }

    // Handle other errors normally
    return Promise.reject(error);
  }
);

export default api;